/**
 * screens/proposals/Proposals.tsx — the trust moment: review AI-drafted rules.
 * Plain-English intent first, confidence meter, reassurance chips, one-click
 * approve (animated → live rule), keyboard A/E/R, and a staged derivation run.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Pressable, Animated, Platform } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { LightColors as C, Radii as R, Shadows as SH, FontSize as FS, FontWeight as FW } from '../../theme/theme';
import { Txt, Row, Cluster, Stack, Eyebrow } from '../../components/ui';
import { Card } from '../../components/card';
import AppModal from '../../components/modal';
import Button from '../../components/button';
import ConfirmDialog from '../../components/confirmDialog';
import SuccessDialog from '../../components/successDialog';
import Chip from '../../components/chip';
import Alert from '../../components/alert';
import Empty from '../../components/empty';
import FilterChip from '../../components/filterChip';
import Spinner from '../../components/spinner';
import Bar from '../../components/bar';
import Icon, { IconName } from '../../components/icon';
import { Page, PageHeader } from '../_layout';

import { Proposal, proposalGuards } from '../../schema/proposal';
import { listProposals, approveProposal, rejectProposal, restoreProposal, deleteProposal, runDerivation } from '../../services/proposals/proposals';
import { useLiveCatalogRefresh } from '../../lib/liveRefresh';
import Switch from '../../components/switch';
import { motionMeta } from '../../domain/motions';
import { toneColors } from '../../theme/tones';
import { useToast } from '../../components/toast';
import { handleApiError } from '../../utils/errorHandler';
import { useCounts } from '../../context/countsContext';
import { useApp } from '../../context/appContext';
import type { AppStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<AppStackParamList>;

const DERIV_STAGES = [
  'Snapshotting catalog…',
  'Inferring business type…',
  'Proposing motions the catalog supports…',
  'Validating IDs & stripping prices…',
  'Writing proposals to the queue…',
];

function confColor(c: number): string {
  if (c >= 0.8) return C.success;
  if (c >= 0.65) return C.info;
  return C.warn;
}


/** Highlight the category words inside the intent — same marker style as Rules. */
function highlightWords(text: string, words: string[]): React.ReactNode {
  const real = words.filter(Boolean);
  if (real.length === 0) return text;
  const escaped = real.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'g');
  return text.split(re).map((part, i) =>
    real.includes(part) ? (
      <Txt key={i} weight={FW.semibold} color={C.ink} style={{ backgroundColor: C.accentSoft, borderRadius: R.r1, paddingHorizontal: 6, paddingVertical: 2 }}>
        {part}
      </Txt>
    ) : (
      part
    ),
  );
}

/** Premium pill action (icon + label) — used for the card's Delete / View-rule
 *  affordances so they read as deliberate controls, not plain text links. */
function PillButton({
  label,
  icon,
  tone = 'neutral',
  onPress,
}: {
  label: string;
  icon: IconName;
  tone?: 'neutral' | 'danger';
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const dc = toneColors('danger');
  // Always render as a crisp, clearly-ENABLED control (dark ink for neutral, solid
  // red for danger) so it stands out next to the greyed, disabled action buttons.
  const bg = tone === 'danger' ? (hover ? dc.line : dc.soft) : hover ? C.paper2 : C.card;
  const border = tone === 'danger' ? dc.fg : C.ink;
  const fg = tone === 'danger' ? dc.fg : C.ink;
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingVertical: 7,
          paddingHorizontal: 13,
          borderRadius: R.full,
          backgroundColor: bg,
          borderWidth: 1,
          borderColor: border,
        },
        (hover ? SH.sm : undefined) as object,
      ]}
    >
      <Icon name={icon} size={13} color={fg} strokeWidth={2.4} />
      <Txt size={12.5} weight={FW.semibold} color={fg}>
        {label}
      </Txt>
    </Pressable>
  );
}

function ProposalCard({
  proposal,
  onApprove,
  onReject,
  onEdit,
  onDelete,
  onRestore,
  restorePending = false,
}: {
  proposal: Proposal;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  restorePending?: boolean;
}) {
  const m = motionMeta(proposal.motion);
  const isRejected = proposal.status === 'rejected';
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const anim = useRef(new Animated.Value(0)).current; // 0 = in, 1 = flown out
  const names = (proposal.categoryExamples ?? []).map((c) => c.name);
  const prettySelect = (proposal.select || 'curated_order').replace(/_/g, ' ');

  useEffect(() => {
    if (proposal.exiting) {
      Animated.timing(anim, { toValue: 1, duration: 340, useNativeDriver: Platform.OS !== 'web' }).start();
    }
  }, [proposal.exiting, anim]);

  return (
    <Animated.View
      style={{
        // Explicit fixed width (capped to the container on small screens) so every
        // card is identical — never content-sized, never stretched to full width.
        width: 460,
        maxWidth: '100%',
        flexGrow: 0,
        flexShrink: 0,
        opacity: anim.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }),
        transform: [{ translateX: anim.interpolate({ inputRange: [0, 1], outputRange: [0, 40] }) }],
      }}
    >
      <Pressable
        onHoverIn={() => setHover(true)}
        onHoverOut={() => setHover(false)}
        style={{ flex: 1 }}
      >
        <Card style={[{ borderRadius: R.r4, flex: 1, borderColor: proposal.stale ? toneColors('warn').line : hover ? C.lineStrong : C.line }, (hover ? SH.md : SH.base) as object]}>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            {/* Accent rail — motion tone, or amber when the proposal is out of date */}
            <View style={{ width: 3, backgroundColor: proposal.stale ? toneColors('warn').fg : toneColors(m.tone).fg, opacity: 0.9 }} />

          <View style={{ flex: 1, padding: 20 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <Row gap={8} style={{ alignItems: 'center' }}>
                <Chip tone={m.tone} dot label={m.label} />
                {proposal.stale && <Chip tone="warn" label="Out of date" />}
              </Row>
              <Row gap={8}>
                <Txt size={FS.xs} color={C.ink3}>
                  confidence
                </Txt>
                <Bar value={proposal.confidence} height={6} fillColor={confColor(proposal.confidence)} style={{ width: 70 }} />
                <Txt size={FS.sm} weight={FW.semibold} color={confColor(proposal.confidence)} style={{ minWidth: 30 }}>
                  {Math.round(proposal.confidence * 100)}%
                </Txt>
              </Row>
            </View>

            {/* Out-of-date banner — a target item/category was removed in Catalog. */}
            {proposal.stale && (
              <Alert tone="warn" style={{ marginBottom: 12 }} iconSize={15}>
                <Txt size={13.5} color={toneColors('warn').fg} lineHeight={20}>
                  <Txt size={13.5} weight={FW.semibold} color={toneColors('warn').fg}>
                    Out of date.{' '}
                  </Txt>
                  {(proposal.staleReason ?? 'a target item is no longer available') + '. '}
                  Approving now would create a rule that can’t make suggestions until it’s back — reject it, or re-run derivation.
                </Txt>
              </Alert>
            )}

            {/* Plain-English intent — category words highlighted. */}
            <Txt size={16} weight={FW.regular} color={C.ink2} lineHeight={26} style={{ marginBottom: 10 }}>
              {highlightWords(proposal.intent, (proposal.categoryExamples ?? []).map((c) => c.name))}
            </Txt>

            {/* Example items — grouped in a soft panel; white pills pop against it. */}
            {proposal.categoryExamples && proposal.categoryExamples.length > 0 && (
              <View
                style={{
                  marginBottom: 12,
                  gap: 7,
                  backgroundColor: C.paper,
                  borderWidth: 1,
                  borderColor: C.line,
                  borderRadius: R.r3,
                  paddingVertical: 11,
                  paddingHorizontal: 12,
                }}
              >
                {proposal.categoryExamples.map((c) => (
                  <Row key={c.name} gap={8} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: R.full, backgroundColor: C.card, borderWidth: 1, borderColor: C.lineStrong }}>
                      <Txt size={11.5} weight={FW.semibold} color={C.ink}>
                        {c.name}
                      </Txt>
                    </View>
                    <Txt size={12.5} color={C.ink2} style={{ flexShrink: 1 }} numberOfLines={1}>
                      {c.items.join(', ')}
                    </Txt>
                  </Row>
                ))}
              </View>
            )}

            {/* Why — a quiet left-accent callout. */}
            <View style={{ marginBottom: 14, borderLeftWidth: 2, borderLeftColor: C.lineStrong, paddingLeft: 11 }}>
              <Txt size={13.5} color={C.ink2} lineHeight={20}>
                <Txt size={FS.xs} weight={FW.semibold} color={C.ink4} transform="uppercase" tracking={0.4}>
                  Why{'  '}
                </Txt>
                {proposal.rationale}
              </Txt>
            </View>

            <Cluster gap={6} style={{ marginBottom: 16 }}>
              {proposalGuards(proposal).map((g) => (
                <Chip
                  key={g}
                  leading={<Icon name="check" size={11} color={C.ink3} strokeWidth={2.4} />}
                  label={g}
                  textStyle={{ color: C.ink3 }}
                />
              ))}
            </Cluster>

            {/* Footer pinned to the card bottom (marginTop:auto) so every card —
                short or tall — has its buttons at the same place with identical
                spacing beneath. paddingTop lifts it off the content above and
                paddingBottom gives the bottom row breathing room from the edge. */}
            <View style={{ marginTop: 'auto', paddingTop: 6, paddingBottom: 8 }}>
              {/* Primary actions */}
              {isRejected ? (
                /* Rejected proposals show a red toggle (easy to spot) to bring
                   them back to Active. */
                <Row gap={10} style={{ alignItems: 'center' }}>
                  <Switch value={restorePending} tone="danger" onValueChange={(next) => { if (next) onRestore?.(); }} />
                  <Txt size={13} weight={FW.semibold} color={C.ink}>
                    Turn on to re-activate
                  </Txt>
                </Row>
              ) : (
                /* Out-of-date proposals are not actionable — a removed catalog
                   target means there's nothing to approve, edit, or reject. */
                <Row gap={10} style={{ alignItems: 'center' }}>
                  <Button label="Approve" variant="success" icon="check" iconStroke={2.6} onPress={onApprove} disabled={proposal.stale} style={{ borderColor: C.ink, flex: 1 }} />
                  <Button label="Edit" onPress={onEdit} disabled={proposal.stale} style={{ borderColor: C.ink, flex: 1 }} />
                  <Button label="Reject" variant="outline" onPress={onReject} disabled={proposal.stale} style={{ borderColor: C.ink, flex: 1 }} />
                </Row>
              )}
              {/* Secondary row — identical on every card: View rule on the LEFT,
                  Delete on the RIGHT (soft-delete; kept in the DB, on all tabs). */}
              <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <PillButton label="View rule" icon="eye" onPress={() => setDetailsOpen(true)} />
                {onDelete ? <PillButton label="Delete" icon="x" tone="danger" onPress={onDelete} /> : <View />}
              </Row>
            </View>

            {/* Premium rule-details modal */}
            <AppModal
              open={detailsOpen}
              onClose={() => setDetailsOpen(false)}
              title="Rule details"
              footer={<Button label="Close" onPress={() => setDetailsOpen(false)} />}
            >
              <Stack gap={18}>
                {/* Header: motion + confidence */}
                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip tone={m.tone} dot label={m.label} />
                  <Row gap={8} style={{ alignItems: 'center' }}>
                    <Txt size={FS.xs} color={C.ink3}>
                      confidence
                    </Txt>
                    <Bar value={proposal.confidence} height={6} fillColor={confColor(proposal.confidence)} style={{ width: 70 }} />
                    <Txt size={FS.sm} weight={FW.semibold} color={confColor(proposal.confidence)}>
                      {Math.round(proposal.confidence * 100)}%
                    </Txt>
                  </Row>
                </Row>

                <View>
                  <Eyebrow style={{ marginBottom: 6 }}>What it does</Eyebrow>
                  <Txt size={16} weight={FW.regular} color={C.ink2} lineHeight={24}>
                    {highlightWords(proposal.intent, names)}
                  </Txt>
                </View>

                {names.length > 0 && (
                  <View style={{ gap: 7, backgroundColor: C.paper, borderWidth: 1, borderColor: C.line, borderRadius: R.r3, paddingVertical: 11, paddingHorizontal: 12 }}>
                    {(proposal.categoryExamples ?? []).map((c) => (
                      <Row key={c.name} gap={8} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: R.full, backgroundColor: C.card, borderWidth: 1, borderColor: C.lineStrong }}>
                          <Txt size={11.5} weight={FW.semibold} color={C.ink}>
                            {c.name}
                          </Txt>
                        </View>
                        <Txt size={12.5} color={C.ink2} style={{ flexShrink: 1 }}>
                          {c.items.join(', ')}
                        </Txt>
                      </Row>
                    ))}
                  </View>
                )}

                {/* Configuration — a clean spec table */}
                <View>
                  <Eyebrow style={{ marginBottom: 8 }}>Configuration</Eyebrow>
                  <View style={{ borderWidth: 1, borderColor: C.line, borderRadius: R.r3 }}>
                    {[
                      { label: 'Type', value: m.label },
                      { label: 'Picks by', value: prettySelect },
                      { label: 'Max price increase', value: `≤ ${proposal.maxPriceDelta}%` },
                      { label: 'Suggestions', value: '1 at a time' },
                    ].map((row, i, arr) => (
                      <View
                        key={row.label}
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingVertical: 11,
                          paddingHorizontal: 14,
                          borderBottomWidth: i < arr.length - 1 ? 1 : 0,
                          borderBottomColor: C.line,
                        }}
                      >
                        <Txt size={13} color={C.ink3}>
                          {row.label}
                        </Txt>
                        <Txt size={13} weight={FW.semibold} color={C.ink} style={{ flexShrink: 1, textAlign: 'right' }} numberOfLines={1}>
                          {row.value}
                        </Txt>
                      </View>
                    ))}
                  </View>
                </View>

                <View>
                  <Eyebrow style={{ marginBottom: 8 }}>Guardrails</Eyebrow>
                  <Cluster gap={6}>
                    {proposalGuards(proposal).map((g) => (
                      <Chip key={g} leading={<Icon name="check" size={11} color={C.ink3} strokeWidth={2.4} />} label={g} textStyle={{ color: C.ink3 }} />
                    ))}
                  </Cluster>
                </View>

                {/* Why — left-accent callout */}
                <View style={{ borderLeftWidth: 2, borderLeftColor: C.lineStrong, paddingLeft: 11 }}>
                  <Txt size={13.5} color={C.ink2} lineHeight={20}>
                    <Txt size={FS.xs} weight={FW.semibold} color={C.ink4} transform="uppercase" tracking={0.4}>
                      Why{'  '}
                    </Txt>
                    {proposal.rationale}
                  </Txt>
                </View>
              </Stack>
            </AppModal>
          </View>
        </View>
        </Card>
      </Pressable>
    </Animated.View>
  );
}

type DerivStatus = 'idle' | 'running' | 'failed';

export default function Proposals() {
  const nav = useNavigation<Nav>();
  const toast = useToast();
  const counts = useCounts();
  const { vertical } = useApp();

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvedToday, setApprovedToday] = useState(0);
  const [deriv, setDeriv] = useState<DerivStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  // Approve / reject confirmation flow.
  const [pendingApprove, setPendingApprove] = useState<Proposal | null>(null);
  const [pendingReject, setPendingReject] = useState<Proposal | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Proposal | null>(null);
  const [pendingRestore, setPendingRestore] = useState<Proposal | null>(null);
  const [resultDialog, setResultDialog] = useState<'approved' | 'rejected' | null>(null);

  const load = useCallback(async () => {
    try {
      setProposals(await listProposals());
    } catch (e) {
      handleApiError(e, 'Could not load proposals');
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load, vertical]),
  );

  // Live updates: while this screen is focused, re-fetch on an interval so a
  // catalog change (item/category/tag deleted → propagated to the projection)
  // moves the affected cards to Deactivated WITHOUT a manual refresh. A tick is
  // skipped while a confirmation dialog is open, a derivation is running, or a
  // card is mid-fly-out, so polling never yanks the list out from under an action.
  const pollBusyRef = useRef(false);
  useEffect(() => {
    pollBusyRef.current =
      deriv !== 'idle' ||
      !!(pendingApprove || pendingReject || pendingDelete || pendingRestore || resultDialog) ||
      proposals.some((p) => p.exiting);
  }, [deriv, pendingApprove, pendingReject, pendingDelete, pendingRestore, resultDialog, proposals]);
  useFocusEffect(
    useCallback(() => {
      const id = setInterval(() => {
        if (!pollBusyRef.current) void load();
      }, 8_000);
      return () => clearInterval(id);
    }, [load]),
  );
  // Instant path: refetch the moment a catalog change is pushed (skip if busy).
  useLiveCatalogRefresh(useCallback(() => {
    if (!pollBusyRef.current) void load();
  }, [load]));

  // Fresh vs out-of-date (deactivated) proposals — separated into tabs, like the
  // Rules screen. A stale proposal (its catalog target was removed) can't be acted
  // on, so it lives in the Deactivated tab with its buttons disabled.
  const [tab, setTab] = useState<'active' | 'deactivated' | 'rejected'>('active');
  const visible = proposals.filter((p) => !p.exiting);
  const rejected = visible.filter((p) => p.status === 'rejected');
  const pendingSet = visible.filter((p) => p.status !== 'rejected');
  const fresh = pendingSet.filter((p) => !p.stale);
  const stale = pendingSet.filter((p) => p.stale);
  const shown = tab === 'rejected' ? rejected : tab === 'deactivated' ? stale : fresh;

  const approve = useCallback(
    (id: string) => {
      const p = proposals.find((x) => x.id === id);
      if (!p || p.exiting) return;
      setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, exiting: true } : x)));
      setTimeout(async () => {
        try {
          await approveProposal(id);
          setProposals((prev) => prev.filter((x) => x.id !== id));
          setApprovedToday((n) => n + 1);
          setResultDialog('approved');
          counts.refresh();
        } catch (e) {
          setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, exiting: false } : x)));
          handleApiError(e, 'Could not approve');
        }
      }, 360);
    },
    [proposals, counts],
  );

  // Reject MOVES the proposal to the Rejected tab (status → rejected) — it's not
  // deleted, so it can be re-activated later.
  const reject = useCallback(
    (id: string) => {
      (async () => {
        try {
          await rejectProposal(id);
          setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'rejected', exiting: false } : x)));
          setResultDialog('rejected');
          counts.refresh();
        } catch (e) {
          handleApiError(e, 'Could not reject');
        }
      })();
    },
    [counts],
  );

  // Restore a rejected proposal back to pending. Fly it out of the Rejected tab,
  // then flip its status so it lands in Active/Deactivated.
  const restore = useCallback(
    (id: string) => {
      setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, exiting: true } : x)));
      setTimeout(async () => {
        try {
          await restoreProposal(id);
          setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'pending', exiting: false } : x)));
          toast.success('Proposal re-activated');
          counts.refresh();
        } catch (e) {
          setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, exiting: false } : x)));
          handleApiError(e, 'Could not re-activate');
        }
      }, 360);
    },
    [toast, counts],
  );

  // Soft-delete: hide from every tab (kept in the DB), with a quick fly-out.
  const remove = useCallback(
    (id: string) => {
      setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, exiting: true } : x)));
      setTimeout(async () => {
        try {
          await deleteProposal(id);
          setProposals((prev) => prev.filter((x) => x.id !== id));
          counts.refresh();
        } catch (e) {
          setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, exiting: false } : x)));
          handleApiError(e, 'Could not delete');
        }
      }, 360);
    },
    [counts],
  );

  const edit = useCallback(
    (id: string) => {
      const p = proposals.find((x) => x.id === id);
      setProposals((prev) => prev.filter((x) => x.id !== id));
      toast.info('Opening this proposal in the Rule Builder');
      // Carry the proposal's real shape into the Builder so the operator edits
      // THIS suggestion (trigger, suggest-from, price cap, phrasing) — not a blank.
      nav.navigate('RuleBuilder', {
        seed: p
          ? {
              motion: p.motion,
              triggerCat: p.triggerCategory,
              excludeCat: p.excludeCategory,
              fromValue: p.from.category,
              select: p.select,
              maxPriceDelta: p.maxPriceDelta,
              phrasing: p.phrasing,
            }
          : undefined,
      });
    },
    [nav, toast, proposals],
  );

  // Keyboard review (web): A approve · E edit · R reject — acts on the top card.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const top = proposals.find((p) => !p.exiting && !p.stale && p.status !== 'rejected');
      if (!top) return;
      const k = e.key.toLowerCase();
      if (k === 'a') setPendingApprove(top);
      else if (k === 'r') setPendingReject(top);
      else if (k === 'e') edit(top.id);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [proposals, approve, reject, edit]);

  const runDeriv = useCallback(() => {
    if (deriv === 'running') return;
    setDeriv('running');
    setProgress(6);
    setStage(DERIV_STAGES[0]);

    // Staged client-side animation (backend returns counts, not a stream).
    let p = 6;
    const timer = setInterval(() => {
      p = Math.min(100, p + Math.round(Math.random() * 16 + 8));
      setProgress(p);
      setStage(DERIV_STAGES[Math.min(DERIV_STAGES.length - 1, Math.floor(p / 22))]);
      if (p >= 100) {
        clearInterval(timer);
        (async () => {
          try {
            const res = await runDerivation();
            await load();
            setDeriv('idle');
            setProgress(0);
            toast.success(`${res.proposals_count} new proposals ready`);
            counts.refresh();
          } catch (e) {
            setDeriv('failed');
            handleApiError(e, 'Derivation failed');
          }
        })();
      }
    }, 320);
  }, [deriv, load, toast, counts]);

  // Only fresh proposals are approvable in bulk (stale ones live in the
  // Deactivated tab and are disabled).
  const bulkApprove = () => fresh.forEach((p, i) => setTimeout(() => approve(p.id), i * 120));

  return (
    <Page>
      <PageHeader
        icon="sparkle"
        eyebrow="AI review"
        title="AI Proposals"
        subtitle="Your catalog, read by AI and drafted into rules. Nothing goes live until you approve it — review each proposal below."
        actions={
          <>
            <Chip
              tone="success"
              lg
              leading={<Icon name="check" size={13} color={C.success} strokeWidth={2.4} />}
              label={`${approvedToday} approved today`}
            />
            <Button label="Run derivation" icon="refresh" variant="accent" onPress={runDeriv} />
          </>
        }
      />

      {/* Derivation running */}
      {deriv === 'running' && (
        <Card style={{ marginTop: 20 }}>
          <View style={{ padding: 22, alignItems: 'center' }}>
            <Row gap={10} style={{ marginBottom: 12 }}>
              <Spinner size={16} />
              <Txt size={15} weight={FW.semibold} color={C.ink}>
                Reading your catalog…
              </Txt>
            </Row>
            <Txt size={FS.md} color={C.ink3} align="center" style={{ maxWidth: 420, marginBottom: 14 }}>
              {stage}
            </Txt>
            <View style={{ width: '100%', maxWidth: 420 }}>
              <Bar value={progress / 100} height={8} fillColor={C.accent} />
            </View>
            <Txt size={FS.xs} color={C.ink4} style={{ marginTop: 10 }} align="center">
              AI runs only at authoring — never on the order. Metered via SK Platform.
            </Txt>
          </View>
        </Card>
      )}

      {/* Derivation failed */}
      {deriv === 'failed' && (
        <Alert tone="danger" style={{ marginTop: 20 }} iconSize={18}>
          <Row gap={8} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Txt size={FS.md} color={C.danger}>
              <Txt size={FS.md} weight={FW.semibold} color={C.danger}>
                Derivation failed.{' '}
              </Txt>
              The model timed out before returning valid JSON. No proposals were written.
            </Txt>
            <Button label="Retry" size="sm" onPress={runDeriv} />
          </Row>
        </Alert>
      )}

      {/* Empty / list */}
      {loading ? (
        <View style={{ paddingVertical: 56, alignItems: 'center' }}>
          <Spinner size={24} />
        </View>
      ) : deriv === 'idle' && visible.length === 0 ? (
        <Empty title="Inbox zero 🎉" actions={<Button label="Run derivation" variant="accent" onPress={runDeriv} />} style={{ marginTop: 20 }}>
          Every proposal has been reviewed. Run a fresh derivation after a menu change to get new ideas.
        </Empty>
      ) : visible.length > 0 ? (
        <>
          {/* Tabs — active · out-of-date (deactivated) · rejected. */}
          <Row gap={8} style={{ marginTop: 20, marginBottom: 14, flexWrap: 'wrap' }}>
            <FilterChip label={`Active (${fresh.length})`} selected={tab === 'active'} onPress={() => setTab('active')} />
            <FilterChip
              label={`Deactivated (${stale.length})`}
              selected={tab === 'deactivated'}
              onPress={() => setTab('deactivated')}
              variant="subtle"
              dotColor={toneColors('warn').fg}
            />
            <FilterChip
              label={`Rejected (${rejected.length})`}
              selected={tab === 'rejected'}
              onPress={() => setTab('rejected')}
              variant="subtle"
              dotColor={toneColors('danger').fg}
            />
          </Row>

          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <Txt size={FS.md} color={C.ink3}>
              <Txt size={FS.md} weight={FW.semibold} color={C.ink}>
                {shown.length}
              </Txt>{' '}
              {tab === 'deactivated'
                ? 'out of date · a catalog item they need was removed'
                : tab === 'rejected'
                ? 'rejected · toggle one on to bring it back to Active'
                : 'pending · reviewed one at a time is the intended path'}
            </Txt>
            {tab === 'active' && fresh.length > 0 && <Button label="Approve all visible" size="sm" onPress={bulkApprove} />}
          </View>

          {shown.length === 0 ? (
            <Empty
              title={tab === 'deactivated' ? 'Nothing out of date' : tab === 'rejected' ? 'No rejected proposals' : 'All caught up'}
              style={{ marginTop: 8 }}
              actions={
                tab === 'active' ? (
                  <Button label="Run derivation" icon="refresh" variant="accent" onPress={runDeriv} />
                ) : undefined
              }
            >
              {tab === 'deactivated'
                ? 'No proposals are blocked by removed catalog items.'
                : tab === 'rejected'
                ? 'Proposals you reject will appear here — you can re-activate them anytime.'
                : 'No pending proposals right now — run a fresh derivation for new ideas.'}
            </Empty>
          ) : (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
              {shown.map((p) => (
                <ProposalCard
                  key={p.id}
                  proposal={p}
                  onApprove={() => setPendingApprove(p)}
                  onReject={() => setPendingReject(p)}
                  onEdit={() => edit(p.id)}
                  onDelete={() => setPendingDelete(p)}
                  onRestore={() => setPendingRestore(p)}
                  restorePending={pendingRestore?.id === p.id}
                />
              ))}
            </View>
          )}
        </>
      ) : null}

      {/* Confirm before approving a proposal into a live rule */}
      <ConfirmDialog
        visible={!!pendingApprove}
        title={pendingApprove?.stale ? 'Approve an out-of-date rule?' : 'Approve this rule?'}
        message={
          pendingApprove?.stale
            ? `This proposal is out of date — ${pendingApprove?.staleReason ?? 'a target item is no longer available'}. It will go live but can’t make suggestions until the item is back.`
            : `"${pendingApprove?.intent ?? 'This rule'}" will go live in Rules and start making suggestions.`
        }
        confirmText={pendingApprove?.stale ? 'Approve anyway' : 'Yes, approve'}
        cancelText="Cancel"
        confirmColor={pendingApprove?.stale ? C.warn : C.success}
        onConfirm={() => {
          const p = pendingApprove;
          setPendingApprove(null);
          if (p) approve(p.id);
        }}
        onCancel={() => setPendingApprove(null)}
        onClose={() => setPendingApprove(null)}
      />

      {/* Confirm before rejecting — reassure it's reversible, not a delete */}
      <ConfirmDialog
        visible={!!pendingReject}
        title="Reject this suggestion?"
        message="It moves to the Rejected tab — you can re-activate it anytime."
        confirmText="Reject"
        cancelText="Cancel"
        confirmColor={C.danger}
        onConfirm={() => {
          const p = pendingReject;
          setPendingReject(null);
          if (p) reject(p.id);
        }}
        onCancel={() => setPendingReject(null)}
        onClose={() => setPendingReject(null)}
      />

      {/* Soft-delete an out-of-date proposal (removed from list, kept in the DB) */}
      <ConfirmDialog
        visible={!!pendingDelete}
        title="Delete this proposal?"
        message={`"${pendingDelete?.intent ?? 'This proposal'}" will be removed from your list. It's kept in your records (soft-deleted), not permanently erased.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor={C.danger}
        onConfirm={() => {
          const p = pendingDelete;
          setPendingDelete(null);
          if (p) remove(p.id);
        }}
        onCancel={() => setPendingDelete(null)}
        onClose={() => setPendingDelete(null)}
      />

      {/* Confirm before re-activating a rejected proposal */}
      <ConfirmDialog
        visible={!!pendingRestore}
        title="Re-activate this suggestion?"
        message="It moves back to the Active tab so you can review and approve it."
        confirmText="Re-activate"
        cancelText="Cancel"
        confirmColor={C.accent}
        onConfirm={() => {
          const p = pendingRestore;
          setPendingRestore(null);
          if (p) restore(p.id);
        }}
        onCancel={() => setPendingRestore(null)}
        onClose={() => setPendingRestore(null)}
      />

      {/* Success after approve / reject */}
      <SuccessDialog
        visible={!!resultDialog}
        tone={resultDialog === 'rejected' ? 'warning' : 'success'}
        title={resultDialog === 'rejected' ? 'Proposal rejected' : 'Approved — now live'}
        message={
          resultDialog === 'rejected'
            ? 'The proposal was discarded.'
            : 'The rule is now active in Rules and will start making suggestions.'
        }
        primaryAction={
          resultDialog === 'rejected'
            ? { label: 'Done', onPress: () => setResultDialog(null) }
            : { label: 'View Rules', onPress: () => { setResultDialog(null); nav.navigate('Rules'); } }
        }
        secondaryAction={resultDialog === 'rejected' ? undefined : { label: 'Keep reviewing', onPress: () => setResultDialog(null) }}
        onClose={() => setResultDialog(null)}
      />
    </Page>
  );
}
